import './OauthAuthorize.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRoute } from 'react-router5';

import {
  Button,
  Header,
  InfoBlock,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Api } from '$api';
import { useAccounts, useCurrentUserInfo, useTranslator } from '$hooks';
import { Loader, UnitName, UnitPhoto } from '$shared/components';
import { authActions } from '$store/auth';
import { profileActions } from '$store/profile';
import { toastActions } from '$store/toast';
import { AuthPopoverState } from '$store/models';
import { IcCheck16, IcPlusCircle28 } from '$assets';
import { ADD_ACCOUNT } from '$shared/constants/pages';
import router from '$navigation/router';

type ClientInfo = { clientId: string; name: string; photo: string };
type InfoResponse = { client: ClientInfo; canAuthorize: boolean; reason: string };

const PERMISSION_KEYS: [string, string][] = [
  ['oauth_perm_messages', 'Read and send messages in your chats'],
  ['oauth_perm_users', 'View your fans and their profiles'],
  ['oauth_perm_files', 'Upload and access files'],
  ['oauth_perm_gifts', 'View gifts'],
  ['oauth_perm_balance', 'View your balance and transactions'],
  ['oauth_perm_albums', 'View and manage your albums'],
  ['oauth_perm_broadcasts', 'View and send broadcasts'],
  ['oauth_perm_updates', 'Receive account updates via webhooks'],
];

export const OauthAuthorize: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { route } = useRoute();
  const params = route.params as Record<string, string | undefined>;
  const currentUser = useCurrentUserInfo();
  const storedAccounts = useAccounts().filter((account) => account.user);
  // Keep stored order; the list is empty when only one account is saved
  const accounts = storedAccounts.some((a) => a.id === currentUser.id)
    ? storedAccounts
    : [{ id: currentUser.id, user: currentUser }, ...storedAccounts];

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [canAuthorize, setCanAuthorize] = useState(false);
  const [reason, setReason] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    Api.get<any, InfoResponse>('/oauth/authorize', {
      params: {
        client_id: params.client_id || '',
        redirect_uri: params.redirect_uri || '',
        response_type: params.response_type || '',
        code_challenge: params.code_challenge || '',
        code_challenge_method: params.code_challenge_method || '',
      },
    })
      .then((data) => {
        setClient(data.client);
        setCanAuthorize(data.canAuthorize);
        setReason(data.reason || '');
        setStatus('ready');
      })
      .catch((e) => {
        if (e?.code !== 'access_token') setStatus('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only ever redirects to the redirect_uri the server has already validated (status === 'ready').
  const redirectBack = useCallback(
    (extra: Record<string, string>) => {
      const url = new URL(params.redirect_uri || '');
      Object.entries(extra).forEach(([k, v]) => url.searchParams.set(k, v));
      if (params.state) {
        url.searchParams.set('state', params.state);
      }
      location.replace(url.toString());
    },
    [params.redirect_uri, params.state],
  );

  const onAllow = useCallback(() => {
    if (isBusy) {
      return;
    }
    setIsBusy(true);
    Api.post<any, { code: string }>('/oauth/authorize', {
      clientId: params.client_id || '',
      redirectUri: params.redirect_uri || '',
      responseType: params.response_type || '',
      codeChallenge: params.code_challenge || '',
      codeChallengeMethod: params.code_challenge_method || '',
      scope: params.scope || '',
      installationId: params.installation_id || '',
    })
      .then(({ code }) => redirectBack({ code }))
      .catch((e) => {
        setIsBusy(false);
        dispatch(
          toastActions.setToastFail(
            e.message ||
              t('oauth_error_generic', {
                defaultValue: 'Something went wrong. Please try again.',
              }),
          ),
        );
      });
  }, [dispatch, isBusy, params, redirectBack, t]);

  const onCancel = useCallback(
    () => redirectBack({ error: 'access_denied' }),
    [redirectBack],
  );

  const onUseAnother = useCallback(() => {
    dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
    router.navigate(ADD_ACCOUNT, { target: location.pathname + location.search });
  }, [dispatch]);

  function renderAccounts() {
    return (
      <div>
        <div className="OauthAuthorize__accountsTitle">
          {t('oauth_account_title', { defaultValue: 'Account' })}
        </div>
        <List>
          {accounts.map((account) => {
            const isCurrent = account.id === currentUser.id;
            return (
              <ListItem
                key={account.id}
                disableHighlight={isCurrent}
                icon={isCurrent ? <IcCheck16 /> : undefined}
                onClick={
                  isCurrent
                    ? undefined
                    : () => dispatch(authActions.changeAccount(account.id))
                }
              >
                <div className="OauthAuthorize__account">
                  <UnitPhoto
                    unit={account.user}
                    nftIndicator={false}
                    onlineIndicator={false}
                    size="extra-small"
                  />
                  <div className="OauthAuthorize__account__text">
                    <UnitName unit={account.user} />
                  </div>
                </div>
              </ListItem>
            );
          })}
          <ListItem onClick={onUseAnother}>
            <div className="OauthAuthorize__account OauthAuthorize__addAccount">
              <div className="OauthAuthorize__addAccount__icon">
                <IcPlusCircle28 />
              </div>
              <span>
                {t('oauth_use_another_account', { defaultValue: 'Use another account' })}
              </span>
            </div>
          </ListItem>
        </List>
      </div>
    );
  }

  function renderContent() {
    if (status === 'loading') {
      return <Loader isWrapped />;
    }

    if (status === 'error' || !client) {
      return (
        <Placeholder
          title={t('oauth_invalid_request_title', { defaultValue: 'Invalid request' })}
        >
          {t('oauth_invalid_request_caption', {
            defaultValue:
              'The application sent an invalid authorization request. Please contact the app developer.',
          })}
        </Placeholder>
      );
    }

    if (!canAuthorize) {
      const banned = reason === 'banned';
      return (
        <div className="OauthAuthorize__cont">
          {renderAccounts()}
          <InfoBlock
            type="warning"
            title={
              banned
                ? t('oauth_banned_title', { defaultValue: 'Account restricted' })
                : t('oauth_not_verified_title', { defaultValue: 'Verification required' })
            }
          >
            {banned
              ? t('oauth_banned_caption', {
                  defaultValue:
                    'Your account is restricted and cannot connect third-party apps.',
                })
              : t('oauth_not_verified_caption', {
                  defaultValue: 'Only verified creators can connect third-party apps.',
                })}
          </InfoBlock>
          <Button mode="secondary" onClick={onCancel}>
            {t('oauth_cancel_button', { defaultValue: 'Cancel' })}
          </Button>
        </div>
      );
    }

    return (
      <div className="OauthAuthorize__cont">
        <div className="OauthAuthorize__app">
          {client.photo && (
            <img className="OauthAuthorize__logo" src={client.photo} alt="" />
          )}
          <div className="OauthAuthorize__appTitle">
            {t('oauth_app_requests_access', {
              name: client.name,
              defaultValue: '%{name} wants to access your ton.place account',
            })}
          </div>
        </div>

        {renderAccounts()}

        <InfoBlock
          type="info"
          title={t('oauth_permissions_title', {
            defaultValue: 'The app will be able to:',
          })}
        >
          <ul className="OauthAuthorize__perms">
            {PERMISSION_KEYS.map(([key, defaultValue]) => (
              <li key={key}>{t(key, { defaultValue })}</li>
            ))}
          </ul>
        </InfoBlock>

        <div className="OauthAuthorize__hint">
          {t('oauth_revoke_hint', {
            defaultValue: 'You can revoke access at any time in Settings → API tokens.',
          })}
        </div>

        <Button onClick={onAllow} disabled={isBusy}>
          {t('oauth_allow_button', { defaultValue: 'Allow access' })}
        </Button>
        <Button mode="secondary" onClick={onCancel}>
          {t('oauth_cancel_button', { defaultValue: 'Cancel' })}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Header>{t('oauth_title', { defaultValue: 'Connect application' })}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
